import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useTransportStore } from '../../../stores/transportStore';
import { useMapStore } from '../../../stores/mapStore';
import { LAYER_IDS, MAP_FOCUS_ZOOM } from '../config/mapConfig';
import type { TraceCaptureMode } from '../../../utils/fieldMappingGps';
import { haversineMetres } from '../../../utils/fieldMappingGps';

interface UseMapInteractionsProps {
  isContributionMode: boolean;
  isDrawingMode: boolean;
  /** When true, MapboxDraw uses line mode instead of polygon mode. */
  drawLineMode?: boolean;
  tracePoints: Array<{ lat: number; lng: number }>;
  traceRingMode?: boolean;
  traceCaptureMode?: TraceCaptureMode;
  /** Tap map to select a trace vertex for delete (refine / edit capture). */
  traceVertexEditEnabled?: boolean;
  selectedTraceVertexIndex?: number | null;
  onTraceVertexSelect?: (index: number | null) => void;
  onContributionClick?: (location: { lat: number; lng: number }) => void;
  /** Fired on draw.create (finish sketch) — e.g. assisted snap. */
  onDrawComplete?: (geometry: GeoJSON.Geometry) => void;
  /** Fired on draw.update / undo / trash — sync React state only, no snap. */
  onDrawGeometryChange?: (geometry: GeoJSON.Geometry) => void;
  onMapClick?: () => void;
}

export function useMapInteractions(
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  {
    isContributionMode,
    isDrawingMode,
    drawLineMode = false,
    tracePoints,
    traceRingMode = false,
    traceCaptureMode = 'continuous',
    traceVertexEditEnabled = false,
    selectedTraceVertexIndex = null,
    onTraceVertexSelect,
    onContributionClick,
    onDrawComplete,
    onDrawGeometryChange,
    onMapClick,
  }: UseMapInteractionsProps
) {
  const contributionMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const onDrawCompleteRef = useRef(onDrawComplete);
  const onDrawGeometryChangeRef = useRef(onDrawGeometryChange);
  const onContributionClickRef = useRef(onContributionClick);
  const onMapClickRef = useRef(onMapClick);
  const onTraceVertexSelectRef = useRef(onTraceVertexSelect);
  const traceVertexEditEnabledRef = useRef(traceVertexEditEnabled);
  const tracePointsRef = useRef(tracePoints);
  const selectedTraceVertexIndexRef = useRef(selectedTraceVertexIndex);
  const isDrawingModeRef = useRef(isDrawingMode);

  useEffect(() => {
    onTraceVertexSelectRef.current = onTraceVertexSelect;
    traceVertexEditEnabledRef.current = traceVertexEditEnabled;
    tracePointsRef.current = tracePoints;
    selectedTraceVertexIndexRef.current = selectedTraceVertexIndex;
    isDrawingModeRef.current = isDrawingMode;
  }, [onTraceVertexSelect, traceVertexEditEnabled, tracePoints, selectedTraceVertexIndex, isDrawingMode]);
  const transportClickHandler = useTransportStore((s) => s.onMapClick);
  const isMapLoaded = useMapStore((s) => s.isMapLoaded);
  /** Throttle camera follow during GPS trace (ms between moves). */
  const traceCameraLastMoveAtRef = useRef(0);

  useEffect(() => {
    onDrawCompleteRef.current = onDrawComplete;
  }, [onDrawComplete]);

  useEffect(() => {
    onDrawGeometryChangeRef.current = onDrawGeometryChange;
  }, [onDrawGeometryChange]);

  useEffect(() => {
    onContributionClickRef.current = onContributionClick;
  }, [onContributionClick]);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  /** Programmatically place the green contribution marker at a given location. */
  const placeMarkerAt = (lat: number, lng: number) => {
    const mapInstance = mapRef.current;
    if (!mapInstance) return;
    if (contributionMarkerRef.current) {
      contributionMarkerRef.current.remove();
      contributionMarkerRef.current = null;
    }
    const el = document.createElement('div');
    el.className = 'contribution-marker';
    el.innerHTML = `<div style="width:36px;height:48px;filter:drop-shadow(0 2px 6px rgba(0,0,0,0.35))"><svg width="36" height="48" viewBox="0 0 36 48" fill="none"><path d="M18 0C9.716 0 3 6.716 3 15c0 10.5 15 33 15 33s15-22.5 15-33C33 6.716 26.284 0 18 0z" fill="#0d9488"/><circle cx="18" cy="15" r="6" fill="white"/></svg></div>`;
    contributionMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([lng, lat])
      .addTo(mapInstance);
  };

  // General map click — dismiss UI overlays + transport stop logging
  useEffect(() => {
    if (!mapRef.current) return;
    const mapInstance = mapRef.current;

    const handleGeneralClick = (e: mapboxgl.MapMouseEvent) => {
      if (!isContributionMode && !isDrawingMode) {
        onMapClickRef.current?.();
      }
      // If a transport collection session is active, log the clicked point as a stop
      if (transportClickHandler && !isContributionMode && !isDrawingMode) {
        const { lat, lng } = e.lngLat;
        transportClickHandler(lat, lng);
      }
    };

    mapInstance.on('click', handleGeneralClick);
    return () => { mapInstance.off('click', handleGeneralClick); };
  }, [onMapClick, isContributionMode, isDrawingMode, transportClickHandler]);

  // Tap a transport stop symbol on the map.
  useEffect(() => {
    if (!mapRef.current) return;
    const mapInstance = mapRef.current;

    const handleTransportStopClick = (e: mapboxgl.MapMouseEvent) => {
      if (isContributionMode || isDrawingMode || transportClickHandler) return;

      const layers = LAYER_IDS.transportStops.filter((id) => {
        try {
          return Boolean(mapInstance.getLayer(id));
        } catch {
          return false;
        }
      });
      if (!layers.length) return;

      const feats = mapInstance.queryRenderedFeatures(e.point, { layers });
      if (!feats.length) return;

      const feat = feats[0];
      if (feat.geometry?.type !== 'Point') return;

      const [lng, lat] = feat.geometry.coordinates as [number, number];
      const props = feat.properties as { name?: string; type?: string; id?: number | string } | null | undefined;
      const name = props?.name?.trim() || props?.type?.replace(/_/g, ' ') || 'Transport stop';

      e.originalEvent?.stopPropagation?.();

      const store = useMapStore.getState();
      if (props?.id != null && String(props.id).trim()) {
        store.selectPlace(`transport_stop:${props.id}`);
        store.setSelectedStopCoords(null);
      } else {
        store.setSelectedStopCoords({ lat, lng, name });
      }
      store.flyToLocation({ lat, lng }, MAP_FOCUS_ZOOM);
      store.setSheetState('collapsed');
    };

    mapInstance.on('click', handleTransportStopClick);
    return () => {
      mapInstance.off('click', handleTransportStopClick);
    };
  }, [isContributionMode, isDrawingMode, transportClickHandler]);

  // Contribution mode click handler
  useEffect(() => {
    if (!mapRef.current) return;

    const mapInstance = mapRef.current;

    const handleContributionClick = (e: mapboxgl.MapMouseEvent) => {
      if (!isContributionMode || !onContributionClickRef.current) return;
      if (traceVertexEditEnabledRef.current) return;

      const { lng, lat } = e.lngLat;

      placeMarkerAt(lat, lng);
      onContributionClickRef.current({ lat, lng });
    };

    if (isContributionMode) {
      mapInstance.getCanvas().style.cursor = 'crosshair';
      mapInstance.on('click', handleContributionClick);
    } else {
      mapInstance.getCanvas().style.cursor = '';
      // Don't remove marker here — it may have been placed programmatically
      // (e.g. coordinates mode). Marker is cleared via explicit clearGeometry.
    }

    return () => {
      mapInstance.off('click', handleContributionClick);
      try {
        const canvas = mapInstance.getCanvas();
        if (canvas) canvas.style.cursor = '';
      } catch {
        // Map may have been destroyed
      }
    };
  }, [isContributionMode, traceVertexEditEnabled]);

  // Trace vertex pick (refine path / edit geometry)
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) return;
    const mapInstance = mapRef.current;

    const pickVertexAtPoint = (e: mapboxgl.MapMouseEvent): number | null => {
      const points = tracePointsRef.current;
      if (points.length === 0) return null;

      const layerIds = ['trace-points-layer', 'trace-point-labels-layer'].filter((id) => {
        try {
          return Boolean(mapInstance.getLayer(id));
        } catch {
          return false;
        }
      });

      if (layerIds.length > 0) {
        const pad = 32;
        const box: [[number, number], [number, number]] = [
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ];
        const hit = mapInstance.queryRenderedFeatures(box, { layers: layerIds });
        for (const feat of hit) {
          const raw = feat.properties?.i;
          if (raw == null) continue;
          const idx = Number(raw);
          if (!Number.isNaN(idx) && idx >= 0 && idx < points.length) return idx;
        }
      }

      let bestIdx = -1;
      let bestPx = Infinity;
      for (let i = 0; i < points.length; i += 1) {
        const projected = mapInstance.project([points[i].lng, points[i].lat]);
        const dx = projected.x - e.point.x;
        const dy = projected.y - e.point.y;
        const d = Math.hypot(dx, dy);
        if (d < bestPx) {
          bestPx = d;
          bestIdx = i;
        }
      }
      if (bestPx <= 48) return bestIdx;

      const { lng, lat } = e.lngLat;
      let bestMIdx = -1;
      let bestM = Infinity;
      for (let i = 0; i < points.length; i += 1) {
        const d = haversineMetres(points[i].lat, points[i].lng, lat, lng);
        if (d < bestM) {
          bestM = d;
          bestMIdx = i;
        }
      }
      return bestM <= 35 ? bestMIdx : null;
    };

    const handleTraceVertexClick = (e: mapboxgl.MapMouseEvent) => {
      if (!traceVertexEditEnabledRef.current) return;
      if (isDrawingModeRef.current) return;
      const idx = pickVertexAtPoint(e);
      e.originalEvent?.stopPropagation?.();
      e.originalEvent?.stopImmediatePropagation?.();
      e.preventDefault();
      if (idx == null) {
        onTraceVertexSelectRef.current?.(null);
        return;
      }
      const current = selectedTraceVertexIndexRef.current;
      onTraceVertexSelectRef.current?.(current === idx ? null : idx);
    };

    mapInstance.on('click', handleTraceVertexClick);
    return () => {
      mapInstance.off('click', handleTraceVertexClick);
    };
  }, [isMapLoaded, traceVertexEditEnabled, mapRef]);

  useEffect(() => {
    if (!mapRef.current) return;
    const mapInstance = mapRef.current;
    if (!traceVertexEditEnabled) return;
    mapInstance.getCanvas().style.cursor = 'pointer';
    return () => {
      try {
        if (!isContributionMode && !isDrawingMode) {
          mapInstance.getCanvas().style.cursor = '';
        }
      } catch {
        // Map may have been destroyed
      }
    };
  }, [traceVertexEditEnabled, isContributionMode, isDrawingMode, mapRef]);

  // Drawing mode
  useEffect(() => {
    if (!mapRef.current) return;

    const mapInstance = mapRef.current;

    if (isDrawingMode) {
      const drawMode = drawLineMode ? 'draw_line_string' : 'draw_polygon';

      if (drawRef.current) {
        try {
          mapInstance.removeControl(drawRef.current);
        } catch {
          // Control may already be removed
        }
        drawRef.current = null;
      }

      drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {},
          defaultMode: drawMode,
          styles: [
            {
              id: 'gl-draw-polygon-fill',
              type: 'fill',
              filter: ['all', ['==', '$type', 'Polygon']],
              paint: { 'fill-color': '#10b981', 'fill-opacity': 0.3 },
            },
            {
              id: 'gl-draw-lines',
              type: 'line',
              filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
              layout: { 'line-cap': 'round', 'line-join': 'round' },
              paint: { 'line-color': '#10b981', 'line-width': 3 },
            },
            {
              id: 'gl-draw-point-outer',
              type: 'circle',
              filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
              paint: {
                'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
                'circle-color': '#fff',
              },
            },
            {
              id: 'gl-draw-point-inner',
              type: 'circle',
              filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
              paint: {
                'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
                'circle-color': '#10b981',
              },
            },
            {
              id: 'gl-draw-vertex-outer',
              type: 'circle',
              filter: [
                'all',
                ['==', '$type', 'Point'],
                ['==', 'meta', 'vertex'],
                ['!=', 'mode', 'simple_select'],
              ],
              paint: {
                'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 10, 7],
                'circle-color': '#fff',
              },
            },
            {
              id: 'gl-draw-vertex-inner',
              type: 'circle',
              filter: [
                'all',
                ['==', '$type', 'Point'],
                ['==', 'meta', 'vertex'],
                ['!=', 'mode', 'simple_select'],
              ],
              paint: {
                'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
                'circle-color': '#2563eb',
              },
            },
            // Omit gl-draw-midpoint — tap-to-add midpoints looked like stray points when selecting vertices.
          ],
        });
      mapInstance.addControl(drawRef.current, 'top-right');

      const handleDrawCreate = (e: { features: GeoJSON.Feature[] }) => {
        if (e.features.length > 0) {
          onDrawCompleteRef.current?.(e.features[0].geometry);
        }
      };

      const handleDrawUpdate = (e: { features: GeoJSON.Feature[] }) => {
        if (e.features.length > 0) {
          onDrawGeometryChangeRef.current?.(e.features[0].geometry);
        }
      };

      mapInstance.on('draw.create', handleDrawCreate);
      mapInstance.on('draw.update', handleDrawUpdate);

      return () => {
        mapInstance.off('draw.create', handleDrawCreate);
        mapInstance.off('draw.update', handleDrawUpdate);
      };
    } else {
      if (drawRef.current) {
        try {
          mapInstance.removeControl(drawRef.current);
        } catch {
          // Control may already be removed
        }
        drawRef.current = null;
      }
    }
  }, [isDrawingMode, drawLineMode]);

  // Live GPS trace — thin dashed line (or closed polygon perimeter) + vertex dots.
  useEffect(() => {
    if (!mapRef.current) return;

    const mapInstance = mapRef.current;
    const sourceId = 'trace-source';
    const layerId = 'trace-layer';
    const fillLayerId = 'trace-fill-layer';
    const pointsLayerId = 'trace-points-layer';
    const pointLabelsLayerId = 'trace-point-labels-layer';
    const MIN_MS_BETWEEN_CAMERA_MOVES = 4000;

    const removeTraceLayers = () => {
      try {
        if (mapInstance.getLayer(pointLabelsLayerId)) mapInstance.removeLayer(pointLabelsLayerId);
        if (mapInstance.getLayer(pointsLayerId)) mapInstance.removeLayer(pointsLayerId);
        if (mapInstance.getLayer(fillLayerId)) mapInstance.removeLayer(fillLayerId);
        if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
        if (mapInstance.getSource(sourceId)) mapInstance.removeSource(sourceId);
        if (mapInstance.getSource('trace-vertices-src')) mapInstance.removeSource('trace-vertices-src');
      } catch {
        // Map may have been destroyed
      }
      traceCameraLastMoveAtRef.current = 0;
    };

    const closeRing = (coords: number[][]) => {
      if (coords.length < 3) return coords;
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] === last[0] && first[1] === last[1]) return coords;
      return [...coords, first];
    };

    const ensureLayer = (id: string, spec: mapboxgl.LayerSpecification) => {
      if (!mapInstance.getLayer(id)) mapInstance.addLayer(spec);
    };

    const updateTrace = () => {
      if (!mapInstance.isStyleLoaded()) {
        mapInstance.once('style.load', updateTrace);
        return;
      }

      if (tracePoints.length === 0) {
        removeTraceLayers();
        return;
      }

      const coordinates = tracePoints.map((p) => [p.lng, p.lat]);
      const ringMode = traceRingMode && tracePoints.length >= 3;
      const geometry: GeoJSON.Geometry = ringMode
        ? { type: 'Polygon', coordinates: [closeRing(coordinates)] }
        : tracePoints.length === 1
          ? { type: 'Point', coordinates: coordinates[0] }
          : { type: 'LineString', coordinates };

      const feature: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry };

      const existing = mapInstance.getSource(sourceId);
      if (existing && 'setData' in existing) {
        (existing as mapboxgl.GeoJSONSource).setData(feature);
      } else {
        removeTraceLayers();
        mapInstance.addSource(sourceId, { type: 'geojson', data: feature });
        if (ringMode) {
          ensureLayer(fillLayerId, {
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            paint: { 'fill-color': '#14b8a6', 'fill-opacity': 0.1 },
          });
        }
        ensureLayer(layerId, {
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#0d9488',
            'line-width': 2,
            'line-opacity': 0.9,
            'line-dasharray': [1.2, 1.2],
          },
        });
      }

      const lastIdx = tracePoints.length - 1;
      const refineMode = traceCaptureMode !== 'corners' && tracePoints.length >= 2;
      const vertices: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: tracePoints.map((p, i) => {
          const endpointLabel =
            refineMode && i === 0
              ? 'Start'
              : refineMode && i === lastIdx
                ? 'End'
                : traceCaptureMode === 'corners'
                  ? String(i + 1)
                  : '';
          const selected = selectedTraceVertexIndex === i;
          return {
            type: 'Feature',
            id: i,
            properties: {
              i,
              label: endpointLabel,
              selected: selected ? 1 : 0,
            },
            geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          };
        }),
      };
      const vtxSrc = mapInstance.getSource('trace-vertices-src');
      const cornerMode = traceCaptureMode === 'corners';
      const showPointLabels = cornerMode || refineMode;
      if (vtxSrc && 'setData' in vtxSrc) {
        (vtxSrc as mapboxgl.GeoJSONSource).setData(vertices);
        if (showPointLabels && !mapInstance.getLayer(pointLabelsLayerId)) {
          ensureLayer(pointLabelsLayerId, {
            id: pointLabelsLayerId,
            type: 'symbol',
            source: 'trace-vertices-src',
            layout: {
              'text-field': ['get', 'label'],
              'text-size': refineMode ? 10 : 11,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'text-offset': refineMode ? [0, 1.2] : [0, 0],
            },
            paint: {
              'text-color': refineMode ? '#0f766e' : '#ffffff',
              'text-halo-color': refineMode ? '#ffffff' : 'transparent',
              'text-halo-width': refineMode ? 1.5 : 0,
            },
          });
        }
      } else {
        if (mapInstance.getLayer(pointLabelsLayerId)) mapInstance.removeLayer(pointLabelsLayerId);
        if (mapInstance.getLayer(pointsLayerId)) mapInstance.removeLayer(pointsLayerId);
        if (mapInstance.getSource('trace-vertices-src')) mapInstance.removeSource('trace-vertices-src');
        mapInstance.addSource('trace-vertices-src', { type: 'geojson', data: vertices });
        ensureLayer(pointsLayerId, {
          id: pointsLayerId,
          type: 'circle',
          source: 'trace-vertices-src',
          slot: 'top',
          paint: {
            'circle-radius': [
              'case',
              ['==', ['get', 'selected'], 1],
              12,
              cornerMode ? 10 : 6,
            ],
            'circle-color': [
              'case',
              ['==', ['get', 'selected'], 1],
              '#2563eb',
              '#0d9488',
            ],
            'circle-stroke-color': '#fff',
            'circle-stroke-width': cornerMode ? 2 : 2,
          },
        });
        if (showPointLabels) {
          ensureLayer(pointLabelsLayerId, {
            id: pointLabelsLayerId,
            type: 'symbol',
            source: 'trace-vertices-src',
            layout: {
              'text-field': ['get', 'label'],
              'text-size': refineMode ? 10 : 11,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'text-offset': refineMode ? [0, 1.2] : [0, 0],
            },
            paint: {
              'text-color': refineMode ? '#0f766e' : '#ffffff',
              'text-halo-color': refineMode ? '#ffffff' : 'transparent',
              'text-halo-width': refineMode ? 1.5 : 0,
            },
          });
        }
      }

      const lastPoint = tracePoints[tracePoints.length - 1];
      const now = Date.now();
      const firstSegment = tracePoints.length <= 2;
      const shouldMoveCamera =
        cornerMode ||
        firstSegment ||
        now - traceCameraLastMoveAtRef.current >= MIN_MS_BETWEEN_CAMERA_MOVES;
      if (shouldMoveCamera) {
        traceCameraLastMoveAtRef.current = now;
        mapInstance.easeTo({
          center: [lastPoint.lng, lastPoint.lat],
          zoom: mapInstance.getZoom(),
          duration: firstSegment ? 550 : 400,
        });
      }
    };

    updateTrace();
  }, [tracePoints, traceRingMode, traceCaptureMode, selectedTraceVertexIndex]);

  const removeMarker = () => {
    if (contributionMarkerRef.current) {
      contributionMarkerRef.current.remove();
      contributionMarkerRef.current = null;
    }
  };

  const syncDrawGeometryToCallback = () => {
    const all = drawRef.current?.getAll();
    if (all?.features.length) {
      const geometry = all.features[all.features.length - 1].geometry;
      if (onDrawGeometryChangeRef.current) {
        onDrawGeometryChangeRef.current(geometry);
      } else {
        onDrawCompleteRef.current?.(geometry);
      }
    }
  };

  return { drawRef, placeMarkerAt, removeMarker, syncDrawGeometryToCallback };
}
